/**
 * The provider stack: the query client and the API client.
 *
 * The TanStack Query defaults here are choices, not boilerplate.
 *
 * - **`retry` never retries `ApiRequestError`.** A 4xx from a contract-
 *   validated API is a disagreement, and repeating it three times turns one
 *   clear failure into three identical log lines. A transport failure is
 *   retried once, because a local server can be mid-restart.
 * - **`gcTime` is set explicitly, and there is no persister.** §6.2: "Cache
 *   only read responses in memory. A full refresh reconstructs from the
 *   backend."
 *   Nothing about the learner may reach browser storage — that row of §6.2's
 *   table is absolute, and a query persister is the single most likely way to
 *   violate it by accident, so the absence is noted here rather than left to
 *   be noticed.
 * - **`refetchOnWindowFocus` is off.** §5's timing instrumentation treats
 *   focus changes as data about the attempt; a refetch that fires on focus
 *   would put network traffic in the middle of that measurement.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { Client } from '@davinci/contracts';
import { ApiRequestError } from '../api/client.js';
import { LocaleProvider } from '../i18n/locale.js';
import { ApiProvider } from './api-context.js';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => !(error instanceof ApiRequestError) && failureCount < 1,
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        // Stated rather than left to the default, because the comment above
        // makes a claim about it. Five minutes, in memory, and then gone.
        gcTime: 5 * 60_000,
      },
      mutations: { retry: false },
    },
  });
}

export interface ProvidersProps {
  readonly api: Client;
  readonly queryClient: QueryClient;
  readonly children: React.ReactNode;
}

export function Providers({ api, queryClient, children }: ProvidersProps): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider value={api}>
        <LocaleProvider>{children}</LocaleProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}
